import { OrderStatus } from "@jti/magento";

import type { FrequencyType } from "./types";

const BASE_STORAGE_KEY = "onsitePersonalizationModal";

const resolveDisplayKey = (popupId?: string) => (popupId ? `${BASE_STORAGE_KEY}_${popupId}` : BASE_STORAGE_KEY);

/**
 * Page-level interaction lock: always uses sessionStorage so it persists
 * across page reloads within the same tab but is cleared when the session
 * ends. Crucially, this is independent of any individual modal's frequencyType,
 * so mixed-frequency sibling modals are blocked consistently.
 */
const PAGE_DISMISSED_SESSION_KEY = (pageKey: string) => `${BASE_STORAGE_KEY}_pageDismissed_${pageKey}`;

const isPageDismissed = (pageKey: string): boolean => {
  try {
    return Boolean(sessionStorage.getItem(PAGE_DISMISSED_SESSION_KEY(pageKey)));
  } catch {
    return false;
  }
};

const recordPageDismissed = (pageKey: string): void => {
  try {
    sessionStorage.setItem(PAGE_DISMISSED_SESSION_KEY(pageKey), "1");
  } catch {
    // sessionStorage unavailable — fail open
  }
};

function writeLocalStorageExpiry(key: string, expiryMs: number): void {
  localStorage.setItem(key, JSON.stringify({ value: true, expireTime: Date.now() + expiryMs }));
}

function writeLocalStoragePermanent(key: string): void {
  localStorage.setItem(key, JSON.stringify({ value: true, expireTime: null }));
}

function isLocalStorageKeyBlocking(key: string): boolean {
  const raw = localStorage.getItem(key);
  if (!raw) return false;
  const parsed: { value: boolean; expireTime: number | null } | null = JSON.parse(raw);
  if (!parsed) return false;
  if (parsed.expireTime === null) return true;
  if (parsed.expireTime > Date.now()) return true;
  localStorage.removeItem(key);
  return false;
}

export type FrequencyConfig = {
  frequencyType?: FrequencyType;
  frequencyValue?: number;
  /** @deprecated Use frequencyType/frequencyValue. Kept for backward compat. */
  hideModalExpireTime?: number;
};

const isPopupBlockedForFrequency = (frequencyType: FrequencyType, displayKey: string): boolean => {
  switch (frequencyType) {
    case "everyVisit":
      return Boolean(sessionStorage.getItem(displayKey));

    case "once":
    case "everyXDays":
    case "showAfterXDays":
    default:
      return isLocalStorageKeyBlocking(displayKey);
  }
};

const recordPopupForFrequency = (frequencyType: FrequencyType, config: FrequencyConfig, displayKey: string): void => {
  switch (frequencyType) {
    case "once":
      writeLocalStoragePermanent(displayKey);
      break;

    case "everyVisit":
      sessionStorage.setItem(displayKey, "shown");
      break;

    case "everyXDays": {
      const days = config.frequencyValue ?? 1;
      const now = Date.now();
      const startOfDayMs = now - (now % (24 * 60 * 60 * 1000));
      writeLocalStorageExpiry(displayKey, startOfDayMs + days * 24 * 60 * 60 * 1000 - now);
      break;
    }

    case "showAfterXDays": {
      const days = config.frequencyValue ?? config.hideModalExpireTime ?? 7;
      writeLocalStorageExpiry(displayKey, days * 24 * 60 * 60 * 1000);
      break;
    }

    default:
      writeLocalStoragePermanent(displayKey);
  }
};

export function shouldShowPopup(config: FrequencyConfig, popupId?: string, pageKey?: string): boolean {
  const displayKey = resolveDisplayKey(popupId);

  if (pageKey && isPageDismissed(pageKey)) {
    return false;
  }

  const frequencyType = config.frequencyType ?? resolveBackwardCompatFrequency(config);

  return !isPopupBlockedForFrequency(frequencyType, displayKey);
}

/**
 * Magento order statuses that count as a completed ("Purchaser") purchase.
 * Source: DESP-140308 / wiki Order Statuses Flow (Magento Statuses).
 *
 * The `status` field on a customer order is returned as the human label
 * (e.g. "Payment completed"), so we feed those labels to the Magento filter.
 */
export const PURCHASER_ORDER_STATUSES: readonly string[] = [
  OrderStatus.payment_completed,
  OrderStatus.shipped,
  OrderStatus.shipped_to_warehouse,
  OrderStatus.delivered,
  OrderStatus.delivered_to_pick_up_point,
];

export function recordPopupShown(config: FrequencyConfig, popupId?: string, pageKey?: string): void {
  const displayKey = resolveDisplayKey(popupId);
  const frequencyType = config.frequencyType ?? resolveBackwardCompatFrequency(config);

  recordPopupForFrequency(frequencyType, config, displayKey);

  if (pageKey) {
    recordPageDismissed(pageKey);
  }
}

function resolveBackwardCompatFrequency(config: FrequencyConfig): FrequencyType {
  if (config.hideModalExpireTime != null) return "showAfterXDays";
  return "once";
}
